/**
 * Haptic feedback utility using the Vibration API
 * Provides tactile feedback on mobile devices for key actions
 */

type HapticPattern = number | number[]

interface HapticFeedback {
  /** Light tap - for button presses, selections */
  light: () => void
  /** Medium tap - for confirmations, important actions */
  medium: () => void
  /** Heavy tap - for errors, warnings */
  heavy: () => void
  /** Success pattern - for completed actions */
  success: () => void
  /** Error pattern - for failed actions */
  error: () => void
  /** Custom pattern */
  pattern: (pattern: HapticPattern) => void
  /** Check if haptics are supported */
  isSupported: () => boolean
}

function vibrate(pattern: HapticPattern): void {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    try {
      navigator.vibrate(pattern)
    } catch {
      // Silently fail if vibration is not available
    }
  }
}

function isSupported(): boolean {
  return typeof navigator !== 'undefined' && 'vibrate' in navigator
}

export const hapticFeedback: HapticFeedback = {
  light: () => vibrate(10),
  medium: () => vibrate(25),
  heavy: () => vibrate(50),
  success: () => vibrate([10, 50, 10]),
  error: () => vibrate([50, 100, 50]),
  pattern: (pattern) => vibrate(pattern),
  isSupported
}

// Default export for convenience
export default hapticFeedback
